import { environment } from '../../../../environments/environment';
import { ApiService } from '../../services/api.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { IRuleResult, RuleStrictness, ComplianceStatus, IRuleCategory, IRuleGroup } from '../../model/rule-engine';
import { BaseComponentOnDestroy } from '../../epics/base-component-on-destroy';
import { catchError, debounceTime, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { PhxSideBarService } from '../../services/phx-side-bar.service';
import { Observable, of } from 'rxjs';
import { UntypedFormBuilder } from '@angular/forms';
import { cloneDeep } from 'lodash';
import { PhxConstants, PhxFormControlLayoutType } from '../../model';
import { GoogleAnalyticsService } from '../../services/google-analytics/google-analytics.service';

@Component({
  selector: 'app-phx-panel-checklist',
  templateUrl: './phx-panel-checklist.component.html',
  styleUrls: ['./phx-panel-checklist.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhxPanelChecklistComponent extends BaseComponentOnDestroy implements OnInit, OnDestroy {

  /** NOTE: used for 'refresh' button spinner visual cue */
  loadingList = true;
  loadingColor = '#333';
  errorLoadingRules = false;

  /** NOTE: entity type being checked - WO, org, onboarding, etc */
  currentEntityValue: any = null;
  entityType: string;

  /** NOTE: list of rule groups */
  ruleGroups: Array<IRuleGroup>;
  /** NOTE: list of rule groups when the user is using keyword filter */
  filteredRuleGroups: Array<IRuleGroup>;

  RuleStrictness = RuleStrictness;
  ComplianceStatus = ComplianceStatus;

  currentComplianceStatus: ComplianceStatus;

  openAccordion: boolean | null = true;
  filterStatus: boolean[] = [true, true, true];

  warningTotal: number;
  compliantTotal: number;
  nonCompliantTotal: number;
  ruleCategories: IRuleCategory[];

  hidingCompliantRules = false;
  hidingWarningRules = false;
  hidingNoncompliantRules = false;
  hidingEverything = false;

  /** NOTE: keyword search form */
  form = this.formBuilder.group({
    keyword: null
  });
  inputOnlyLayoutType: PhxFormControlLayoutType = PhxFormControlLayoutType.InputOnly;

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private phxSidebarService: PhxSideBarService,
    private formBuilder: UntypedFormBuilder,
    private googleAnalyticsService: GoogleAnalyticsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initEntityTypeChange();
    this.initEntityChange();
    this.initRefreshChecklistEmit();
    this.initKeyWordChange();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  toggleSwitchAllOff(): void {
    this.openAccordion = null;
  }

  /** NOTE: user can trigger a refresh - 'refresh' button */
  refreshChecklist(currentEntity?: any) {
    this.loadingList = true;
    this.getCompliancyRuleResults(currentEntity).pipe(
      take(1)
    ).subscribe();
    this.sendGoogleClickData('Refresh checklist');
  }

  trackByFn(index: number, item: IRuleGroup) {
    return item.category;
  }

  resetFilters() {
    this.filterStatus = [true, true, true];
    this.applyFiltersAndSearch();
    this.openAccordion = true;
  }

  applyFiltersAndSearch() {
    let temporaryFilter: Array<IRuleResult> = [];
    this.filteredRuleGroups = cloneDeep(this.ruleGroups);

    this.hidingEverything = false;

    this.hidingCompliantRules = !this.filterStatus[0];
    this.hidingWarningRules = !this.filterStatus[2];
    this.hidingNoncompliantRules = !this.filterStatus[1];


    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < this.filteredRuleGroups.length; i++) {
      temporaryFilter = [];
      for (let j = 0; j < this.filterStatus.length; j++) {
        if (j === 0 && this.filterStatus[j] === true) {
          temporaryFilter.push(...this.getCompliantRules(this.filteredRuleGroups[i].rules));
        }
        else if (j === 1 && this.filterStatus[j] === true) {
          temporaryFilter.push(...this.getNonCompliantRules(this.filteredRuleGroups[i].rules));
        }
        else if (j === 2 && this.filterStatus[j] === true) {
          temporaryFilter.push(...this.getWarningRules(this.filteredRuleGroups[i].rules));
        }
      }

      if (this.form.controls.keyword.value) {
        this.filteredRuleGroups[i].rules = temporaryFilter
          .filter(x => x.RuleText.toLowerCase().includes(this.form.controls.keyword.value?.toLowerCase()))
          .sort((r1, r2) => r1.SortOrder - r2.SortOrder);
      } else {
        this.filteredRuleGroups[i].rules = temporaryFilter.sort((r1, r2) => r1.SortOrder - r2.SortOrder);
      }
    }

    if (!this.filteredRuleGroups.some(m => m.rules?.length > 0)) {
      this.hidingEverything = true;
    }
  }

  /** NOTE: the use of this is temporary to assess the usage of the compliancy 
   * checklist action items - mar 4/24 -  will eventually be removed - story #43109 */
  sendGoogleClickData(clickAction: string) {
    this.googleAnalyticsService.sendClickData({
      feature: 'Compliancy checklist',
      type: this.entityType,
      action: clickAction,
    });
  }

  /** NOTE: each entity type has it's own rule categories - on change get those categories */
  private initEntityTypeChange() {
    this.phxSidebarService.entityTypeChange$().pipe(
      filter(entityType => !!entityType),
      switchMap(entityType => {
        this.entityType = entityType;
        return this.apiService.httpGetRequest<IRuleCategory[]>(`RuleEngine/GetRuleCategories/${entityType}`, environment.ruleEngineApiEndpoint);
      }),
      takeUntil(this.isDestroyed$)
    ).subscribe(ruleCategories => {
      this.ruleCategories = ruleCategories;
    });
  }

  /** NOTE: when the entity changes we need to validate its compliancy against the rule server  */
  private initEntityChange() {
    /** NOTE: 2 configurations - run rules each time entity changes or run rules once onload, then user manually runs rules by clicking refresh button */
    if (environment.instantRefreshChecklist === 'true') {
      this.phxSidebarService.entityChange$().pipe(
        switchMap(changedEntity => this.getCompliancyRuleResults(changedEntity)),
        takeUntil(this.isDestroyed$)
      ).subscribe();
    } else {
      this.phxSidebarService.entityChange$().pipe(
        switchMap(changedEntity => this.getCompliancyRuleResults(changedEntity)),
        take(1)
      ).subscribe();
    }
  }

  /** NOTE: components can emit a refresh using the sidebar service so we need to watch it */
  private initRefreshChecklistEmit() {
    this.phxSidebarService.onRefresh.pipe(
      switchMap(entity => this.getCompliancyRuleResults(entity)),
      takeUntil(this.isDestroyed$)
    ).subscribe();
  }

  /** NOTE: filter compliancy checklistlist as the user types a search keyword */
  private initKeyWordChange() {
    this.form.controls.keyword.valueChanges.pipe(
      takeUntil(this.isDestroyed$),
      tap(() => this.applyFiltersAndSearch()),
      debounceTime(1000),
    ).subscribe(searchVal => {
      if (searchVal) {
        this.sendGoogleClickData(`Search: ${searchVal}`);
      }
    });
  }

  /** NOTE: get rule engine compliancy for entity passed in or the current entity */
  private getCompliancyRuleResults(entity: any): Observable<IRuleResult[]> {
    this.errorLoadingRules = false;
    const entityToValidate = entity || this.currentEntityValue;
    return (entityToValidate ?
      this.apiService.httpPostRequest<IRuleResult[]>(`RuleEngine/RunRuleSet/${this.entityType}/Compliance`, { Entity: entityToValidate }, environment.ruleEngineApiEndpoint, false).pipe(
        tap(() => {
          this.loadingList = false;
          this.cdr.detectChanges();
        }),
        catchError(() => {
          this.loadingList = false;
          this.errorLoadingRules = true;
          this.cdr.detectChanges();
          return of([]);
        })
      )
      :
      of([])).pipe(
        filter(ruleResult => !!ruleResult?.length),
        tap((ruleResult) => {
          this.currentEntityValue = entityToValidate;
          this.configureRules(ruleResult);
          this.loadingList = false;
          this.cdr.detectChanges();
        })
      );
  }

  private configureRules(ruleResults: IRuleResult[]) {
    this.createRuleGroups(ruleResults);
    this.calculateTotals();
    this.applyFiltersAndSearch();
  }

  /** NOTE: group the rule result into the categories pulled above in this.initEntityTypeChange */
  private createRuleGroups(ruleResults: IRuleResult[]) {
    const rulesGroupedByCategory = this.groupRulesByProperty(ruleResults, 'RuleCategory');

    this.ruleGroups = Object.keys(rulesGroupedByCategory).map(key => {
      const rules = rulesGroupedByCategory[key] as Array<IRuleResult>;

      const compliantCount = (this.getCompliantRules(rules)).length;
      const warningCount = (this.getWarningRules(rules)).length;
      const nonCompliantCount = (this.getNonCompliantRules(rules)).length;

      const groupCompliance = nonCompliantCount > 0 ? ComplianceStatus.NonCompliant : (warningCount > 0 ? ComplianceStatus.Warning : ComplianceStatus.Compliant);

      return {
        category: this.ruleCategories?.find(x => x.Id === key)?.RuleCategoryText,
        length: rules?.length,
        rules: rules?.sort(rule => rule.SortOrder),
        compliantCount,
        warningCount,
        nonCompliantCount,
        groupCompliance,
        categorySortOrder: this.ruleCategories?.find(x => x.Id === key)?.SortOrder || 0
      };
    })?.sort((a, b) => a.categorySortOrder - b.categorySortOrder);

    /** NOTE: get the current compliancy of this entity*/
    this.currentComplianceStatus = Math.max(...this.ruleGroups.map(o => o.groupCompliance));
    this.phxSidebarService.updateEntityStatus(PhxConstants.StatusType.Types?.[this.currentComplianceStatus.valueOf()]);
  }

  /** NOTE: group the list of rules by the property passed in */
  private groupRulesByProperty = (ruleResults: IRuleResult[], key: string) => {
    return ruleResults.filter(x => x.Strictness !== RuleStrictness.NotApplicable).reduce((rv, x) => {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});
  };

  private getNonCompliantRules(ruleResults: IRuleResult[]) {
    return ruleResults.filter(o => !o.IsValid && (o.Strictness === RuleStrictness.Mandatory || o.Strictness === RuleStrictness.Regular));
  }

  private getWarningRules(ruleResults: IRuleResult[]) {
    return ruleResults.filter(o => !o.IsValid && o.Strictness === RuleStrictness.Warning);
  }

  private getCompliantRules(ruleResults: IRuleResult[]) {
    return ruleResults.filter(o => o.IsValid || o.Strictness === RuleStrictness.NotApplicable);
  }

  /** NOTE: calculate total counts for each rule type */
  private calculateTotals() {
    this.warningTotal = 0;
    this.compliantTotal = 0;
    this.nonCompliantTotal = 0;

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < this.ruleGroups.length; i++) {
      this.compliantTotal += this.ruleGroups[i].compliantCount;
      this.nonCompliantTotal += this.ruleGroups[i].nonCompliantCount;
      this.warningTotal += this.ruleGroups[i].warningCount;
    }

    this.phxSidebarService.checklistRefreshed.emit({ nonCompliantCount: this.nonCompliantTotal });
  }

}

